import { useForm } from 'react-hook-form';

import { useRootSelector } from '../../../../../../store/hooks';
import { usePackageSelectionHandlers } from '../../../../hooks/selectPackage/usePackageSelectionHandlers';
import { SelectPackageProvider } from './selectPackageContext';
import { PricingPackagesFormValues } from '../../../../store/features/packages/types';
import { selectSitePricingProductsData } from '../../../../store/features/products';
import { selectSiteType } from '../../../../../../store/features/siteType';
import { selectPackageFormDefaultValuesData } from '../../../../store/features/packageFormDefaultValues';
import { SiteTypes } from '../../../../../../types';
import { findFirstPreselectedPrimaryProduct } from '../../../../helpers/selectPackage/getPreselectedPackageFormValues';

import FormContainer from '../../../../shared/formElements/FormContainer';
import Section from '../../../../shared/layouts/Section';
import SectionHeader from '../../../../shared/components/SectionHeader';
import DelegateTypesTabs from '../DelegateTypesTabs';
import CourseOptionsTabs from '../CourseOptionsTabs';
import PackageSelectionPanel, {
  PackageSelectionPanelProps,
} from '../PackageSelectionPanel';
import ProductSelectionRightColumn from '../ProductSelectionRightColumn';
import KeyInformationSection from '../KeyInformationSection';
import TierDiscountBanner from '../TierDiscountBanner';
import VirtualVatForm from '../VirtualVatForm';
import { MuiStack } from '../../../../../../modules/material';

export default function PackageSelectionForm() {
  const { type: siteType } = useRootSelector(selectSiteType);
  const productsData = useRootSelector(selectSitePricingProductsData);
  const packageFormDefaultValues = useRootSelector(
    selectPackageFormDefaultValuesData,
  );

  const form = useForm<PricingPackagesFormValues>({
    defaultValues: packageFormDefaultValues,
  });

  const {
    handleAddSingleProductToBasket,
    handleAddMultipleProductsToBasket,
    handleSelectProduct,
    handleUnselectProduct,
    handleChangeSelections,
    handleValidateVipCode,
    resetProductSelections,
    setSelectedProductId,
    selectedProductId,
    addedToBasketProductId,
  } = usePackageSelectionHandlers<PricingPackagesFormValues>({
    form,
    preselectedProduct: findFirstPreselectedPrimaryProduct(
      productsData.productTypesWithProducts,
    ),
  });

  const handlePrimaryProductCardClick: PackageSelectionPanelProps['onClickPrimaryProduct'] =
    async (isSecondLevelAvailable, product, productIndex) => {
      if (isSecondLevelAvailable && handleSelectProduct) {
        handleSelectProduct(product, productIndex);
      } else if (handleAddSingleProductToBasket) {
        setSelectedProductId(null);
        const isValid = await form.trigger(['country', 'vatNumber', 'region']);

        if (isValid) {
          handleAddSingleProductToBasket(product.id);
        }
      }
    };

  return (
    <SelectPackageProvider handleChangeSelections={handleChangeSelections}>
      <FormContainer<PricingPackagesFormValues>
        form={form}
        onSubmit={handleAddMultipleProductsToBasket}
      >
        <Section
          marginVariant="extraSmall"
          showEmptyRightColumn
          className="informa-payment-select-package-header-section"
        >
          <SectionHeader
            heading={productsData.productTitle}
            headingProps={{
              paletteColorName: 'brandSecondary',
            }}
            description={productsData.packageDescription}
          />
        </Section>

        <Section
          rightColumn={
            <ProductSelectionRightColumn
              onValidateVipCode={handleValidateVipCode}
            />
          }
          className="informa-payment-select-package-panel-section"
        >
          <MuiStack spacing={4} sx={{ mb: 6 }}>
            {siteType === SiteTypes.EVENT && (
              <DelegateTypesTabs
                onChangeTab={(delegateId) => {
                  resetProductSelections({
                    delegateTypeId: delegateId,
                    shouldSetPreselectedProductId: true,
                  });
                }}
              />
            )}

            {siteType === SiteTypes.COURSE && (
              <CourseOptionsTabs
                defaultValue={packageFormDefaultValues.siteOptionIndex}
                onChange={(event, optionIndex) => {
                  resetProductSelections({
                    siteOptionIndex: optionIndex,
                    shouldSetPreselectedProductId: true,
                  });
                }}
              />
            )}

            <VirtualVatForm />

            <TierDiscountBanner />
          </MuiStack>

          <PackageSelectionPanel
            selectedProductId={selectedProductId}
            addedToBasketProductId={addedToBasketProductId}
            onClickPrimaryProduct={handlePrimaryProductCardClick}
            onCollapsePrimaryProduct={handleUnselectProduct}
          />
        </Section>

        <Section
          showEmptyRightColumn
          className="informa-payment-select-package-faq-section"
        >
          <KeyInformationSection />
        </Section>
      </FormContainer>
    </SelectPackageProvider>
  );
}
